import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Button, Divider, Paper, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router';
import {getReportById, updateReportDoc} from '../../../../firebase/firestore/reports';
import ReportPreviewPage from '../../../../features/products/previewPage/ReportPreviewPage';
import DisclosurePreviewPage from '../../../../features/products/previewPage/DisclosurePreviewPage';
import LoadingInTheMiddle from '../../../../components/LoadingInTheMiddle';
import DisclosureHeader from '../../components/DisclosureHeader';
import {useReportStore} from '../../../../features/products/useReportStore';

const STATUS = {
    APPROVED: 'approved',
};

const DisclosureReviewSplitPage = ({backPath = '/disclosures'}) => {
    const {reportId} = useParams();
    const navigate = useNavigate();
    const setReport = useReportStore(s => s.setReport);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [disclosure, setDisclosure] = useState(null);

    const parentReportId = useMemo(() => {
        return disclosure?.parent_product?.uid || disclosure?.parentReportId || null;
    }, [disclosure]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(null);
        setDisclosure(null);

        const load = async () => {
            try {
                const disclosureDoc = await getReportById(reportId);
                if (!mounted) return;
                setDisclosure(disclosureDoc);
                setReport(disclosureDoc || {});
            } catch (e) {
                console.error('Failed to load disclosure review data', e);
                if (mounted) setError(e);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        if (reportId) load();
        else setLoading(false);

        return () => {
            mounted = false;
        };
    }, [reportId, setReport]);

    const status = disclosure?.status;

    const persistStatus = async (nextStatus) => {
        if (!reportId) return;
        try {
            setSaving(true);
            await updateReportDoc(reportId, {status: nextStatus});
            setDisclosure((prev) => ({...(prev || {}), status: nextStatus}));
            setReport({...((disclosure || {})), status: nextStatus});
        } catch (e) {
            console.error('Failed to update disclosure status', e);
            setError(e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingInTheMiddle/>;

    if (!reportId) {
        return (
            <Stack p={3} spacing={2}>
                <Alert severity="error">Missing reportId in route</Alert>
            </Stack>
        );
    }

    if (!disclosure) {
        return (
            <Stack p={3} spacing={2}>
                <Alert severity="error">Disclosure not found</Alert>
                <Button variant="contained" onClick={() => navigate(backPath)}>Back to list</Button>
            </Stack>
        );
    }

    return (
        <Stack height="100vh" minHeight={0}>
            <DisclosureHeader
                title={disclosure?.title}
                status={status}
                backPath={backPath}
                saving={saving}
                onApprove={() => persistStatus(STATUS.APPROVED)}
                onReject={() => persistStatus('Draft')}
            />

            <Divider/>

            {error && (
                <Stack p={2}>
                    <Alert severity="error">{String(error?.message || error)}</Alert>
                </Stack>
            )}

            <Stack direction="row" flex={1} minHeight={0}>
                {/* Left: parent report */}
                <Stack flex={1} minWidth={0} borderRight={'1px solid'} borderColor={'divider'}>
                    <Stack p={1.5}>
                        <Typography variant="subtitle2" color="text.secondary">Parent report</Typography>
                        {!parentReportId && (
                            <Typography variant="caption" color="text.secondary">
                                No parent report linked on this disclosure.
                            </Typography>
                        )}
                    </Stack>
                    <Divider/>
                    <Stack flex={1} minHeight={0}>
                        {parentReportId ? (
                            <ReportPreviewPage reportId={parentReportId} hideVersionSidebar/>
                        ) : (
                            <Stack p={2}>
                                <Paper variant="outlined" sx={{p: 2}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nothing to show.
                                    </Typography>
                                </Paper>
                            </Stack>
                        )}
                    </Stack>
                </Stack>

                {/* Right: disclosure */}
                <Stack flex={1} minWidth={0}>
                    <Stack p={1.5}>
                        <Typography variant="subtitle2" color="text.secondary">Disclosure</Typography>
                    </Stack>
                    <Divider/>
                    <Stack flex={1} minHeight={0}>
                        <DisclosurePreviewPage reportId={reportId} hideVersionSidebar/>
                    </Stack>
                </Stack>
            </Stack>
        </Stack>
    );
};

export default DisclosureReviewSplitPage;
